import React, { useMemo } from "react";

import {
  OperationActions,
  operationColumns,
  useOperationConfirm,
} from "src/adminPanel/modulesV2/OperationRow";
import { PatchViewerModal } from "src/adminPanel/modulesV2/PatchViewerModal";
import {
  ModuleOperation,
  transformOperation,
  useInfiniteModuleOperations,
} from "src/api/modulesApi";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { TableComp } from "src/base-components/Table";
import { Modal, useModal } from "src/design-system/Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave: () => void;
  moduleId: string;
  moduleKind: string;
  workspaceName?: string;
  orgName?: string;
};

export const AllOperationsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  moduleId,
  moduleKind,
  workspaceName,
  orgName,
}) => {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteModuleOperations(moduleId, { enabled: isOpen });

  const {
    isOpen: isPatchOpen,
    openModal: openPatchModal,
    closeModal: closePatchModal,
    afterLeave: afterPatchLeave,
    data: selectedOperation,
  } = useModal<ModuleOperation>();

  const {
    confirmAction,
    isConfirmOpen,
    openConfirm,
    handleConfirm,
    clearConfirm,
    closeConfirm,
  } = useOperationConfirm();

  const operations: ModuleOperation[] = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        (Array.isArray(page.data) ? page.data : [page.data]).map(
          transformOperation,
        ),
      ) ?? [],
    [data],
  );

  const subtitle = [orgName, workspaceName, moduleKind]
    .filter(Boolean)
    .join(" · ");

  const actions: OperationActions = useMemo(
    () => ({
      onView: openPatchModal,
      onCancel: (op: ModuleOperation) =>
        openConfirm({ operation: op, type: "cancel" }),
      onRetry: (op: ModuleOperation) =>
        openConfirm({ operation: op, type: "retry" }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Modal afterLeave={afterLeave} open={isOpen} size="lg" onClose={onClose}>
        <Modal.Header description={subtitle}>All Operations</Modal.Header>
        <Modal.Content noScroll>
          <div className="-mx-5 max-h-[500px] overflow-auto px-5">
            <TableComp
              actions={actions}
              columns={operationColumns}
              data={operations}
              disableVirtualization
              emptyState={
                <div className="py-8 text-center font-inter-normal-13px text-gray-500">
                  No operations found
                </div>
              }
              getRowId={(row) => row.id}
              headerPropsGetter={() => ({
                classNameOverrides: "py-3",
              })}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              variant="default"
              onScrolledToTheBottom={
                hasNextPage ? () => fetchNextPage() : undefined
              }
            />
          </div>
        </Modal.Content>
        <Modal.Footer primaryButton={false} secondaryButton="Close" />
      </Modal>

      <PatchViewerModal
        afterLeave={afterPatchLeave}
        isOpen={isPatchOpen}
        operation={selectedOperation}
        onClose={closePatchModal}
      />

      <ConfirmationModal
        confirmationButtonText={
          confirmAction?.type === "cancel"
            ? "Cancel Operation"
            : "Retry Operation"
        }
        description={
          confirmAction ? (
            <span>
              Are you sure you want to{" "}
              {confirmAction.type === "cancel" ? "cancel" : "retry"} operation{" "}
              <span className="font-inter-semibold-13px">
                {confirmAction.operation.id.slice(0, 8)}
              </span>
              ?
            </span>
          ) : undefined
        }
        afterLeave={clearConfirm}
        open={isConfirmOpen}
        title={
          confirmAction?.type === "cancel"
            ? "Cancel Operation"
            : "Retry Operation"
        }
        variant={confirmAction?.type === "cancel" ? "danger" : undefined}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
      />
    </>
  );
};
